import React from 'react';
import PropTypes from 'prop-types';
import { JsonLd } from 'react-schemaorg';
import { globals } from '../../config/globals';
import Head from 'next/head';

export const buildTitle = (props) =>
  props.fullTitle
    ? props.fullTitle
    : props.seoHeadline
    ? `${props.seoHeadline} | ${globals.siteName}`
    : props.title
    ? `${props.title} | ${globals.siteName}`
    : globals.siteName;

const Metatags = (props) => {
  const combinedTitle = buildTitle(props);

  // An empty-string fullSlug (e.g. the homepage) resolves to the bare host so
  // it still gets a canonical URL; only a null/undefined fullSlug opts out.
  const fullUrl =
    typeof props.fullSlug === 'string'
      ? props.fullSlug
        ? `${globals.hostnameProd}/${props.fullSlug}`
        : globals.hostnameProd
      : null;
  let canonicalUrl = props.originalSourceUrl
    ? props.originalSourceUrl
    : fullUrl;
  if (canonicalUrl && props.pageNum && props.pageNum > 1) {
    canonicalUrl += `/${props.pageNum}`;
  }
  const socialTitle = props.shortTitle ? props.shortTitle : props.title;
  const seoTitle = props.seoHeadline ? props.seoHeadline : props.title;
  const authors = props.authors
    ? props.authors.reduce((acc = [], curr) => {
        acc.push({
          '@type': 'Person',
          name: curr.title,
          ...(curr.as && !curr.as.includes('undefined')
            ? { url: `${globals.hostnameProd}${curr.as}` }
            : {}),
        });
        return acc;
      }, [])
    : [{ '@type': 'Organization', name: 'MPR News' }];
  const datePublished = props.publishDate;
  const dateModified = props.updatedAt || datePublished;
  const tags =
    props.publishedTags && props.publishedTags.length > 0
      ? props.publishedTags.join(', ')
      : undefined;

  return (
    <Head>
      <title>{combinedTitle}</title>
      {/* Google Rich Results */}
      {props.contentType === 'article' && (
        <>
          <JsonLd
            item={{
              '@context': 'https://schema.org',
              '@type': 'NewsArticle',
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': fullUrl,
              },
              headline: props.title,
              image: [props.image],
              datePublished: datePublished,
              dateModified: dateModified,
              description: props.description.trim(),
              author: authors.length > 1 ? authors : authors[0],
              keywords: tags,
              wordCount: props.wordcount,
              ...(props.articleSection
                ? { articleSection: props.articleSection }
                : {}),
              publisher: {
                '@type': 'NewsMediaOrganization',
                name: 'MPR News',
                ethicsPolicy: 'https://www.mprnews.org/ethics',
                logo: {
                  '@type': 'ImageObject',
                  url: 'https://www.mprnews.org/mpr-news-1080x1080.png',
                },
              },
            }}
          />
        </>
      )}
      {props.noFollow && (
        <meta key="robots" name="robots" content="noindex,nofollow" />
      )}

      {props.noFollow && (
        <meta key="googlebot" name="googlebot" content="noindex,nofollow" />
      )}
      {canonicalUrl && (
        <>
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:url" content={canonicalUrl} key="og:url" />
        </>
      )}
      {/* noindex syndicated content (NPR) */}
      {canonicalUrl && canonicalUrl.includes('npr') && (
        <>
          <meta key="robots" name="robots" content="noindex,nofollow" />
          <meta key="googlebot" name="googlebot" content="noindex,nofollow" />
        </>
      )}
      {props.topic && (
        <meta
          name="mpr-content-topic"
          content={props.topic}
          key="mpr-content-topic"
        />
      )}
      {socialTitle && (
        <>
          <meta name="title" content={seoTitle} key="title" />
          <meta
            name="twitter:title"
            content={props.title}
            key="twitter:title"
          />
          <meta property="og:title" content={props.title} key="og:title" />
        </>
      )}
      {tags && (
        <meta name="keywords" content={props.publishedTags.join(', ')} />
      )}
      {props.description && (
        <>
          <meta
            name="description"
            content={props.description.trim()}
            key="description"
          />
          <meta
            name="twitter:description"
            content={props.description.trim()}
            key="twitter:description"
          />
          <meta
            property="og:description"
            content={props.description.trim()}
            key="og:description"
          />
        </>
      )}
      {props.rssUrl && (
        <link
          title={props.title}
          href={props.rssUrl}
          rel="alternate"
          type="application/rss+xml"
        />
      )}
      {props.image && (
        <>
          <meta
            name="twitter:image"
            content={props.image}
            key="twitter:image"
          />
          <meta property="og:image" content={props.image} key="og:image" />
          {props.imageWidth && (
            <meta
              property="og:image:width"
              content={props.imageWidth}
              key="og:image:width"
            />
          )}
          {props.imageHeight && (
            <meta
              property="og:image:height"
              content={props.imageHeight}
              key="og:image:height"
            />
          )}
          {props.imageAlt && (
            <meta
              property="og:image:alt"
              content={props.imageAlt}
              key="og:image:alt"
            />
          )}
        </>
      )}
      {props.imageAlt && (
        <meta name="twitter:image:alt" content={props.imageAlt} />
      )}
      {props.contentType && (
        <meta property="og:type" content={props.contentType} key="og:type" />
      )}
      {/* Twitter do not track */}
      <meta name="twitter:dnt" content="on" />
      {/* Google Search Console/Webmaster Tools Verifications */}
      <meta
        name="google-site-verification"
        content="3mdQkyZQ0hy2oLHqV_shtyaKyvb-xVslxgr2kdV8RQw"
      />
      {/* FB app ID */}
      <meta property="fb:pages" content="99142348590" />
      {!props.noFollow && (
        <meta name="robots" content="max-image-preview:large" />
      )}
      {/* Any custom meta tags */}
      {props.children}
    </Head>
  );
};

Metatags.propTypes = {
  fullSlug: PropTypes.string, // This should include any routing prefixes, e.g. '/story'
  children: PropTypes.node,
  contentType: PropTypes.oneOf(['article', 'profile', 'website']),
  description: PropTypes.string,
  image: PropTypes.string,
  imageAlt: PropTypes.string,
  imageWidth: PropTypes.number,
  imageHeight: PropTypes.number,
  title: PropTypes.string,
  seoHeadline: PropTypes.string,
  shortTitle: PropTypes.string,
  rssUrl: PropTypes.string,
  topic: PropTypes.string,
  publishDate: PropTypes.string,
  publishedTags: PropTypes.array,
  updatedAt: PropTypes.string,
  authors: PropTypes.array,
  noFollow: PropTypes.bool,
  originalSourceUrl: PropTypes.string,
  pageNum: PropTypes.number,
  wordcount: PropTypes.number,
  articleSection: PropTypes.string,
};

export default Metatags;
